// assets/new-design-variant-swap.js
//
// Instant variant switching.
//
// Dawn resolves a variant change over the network: variant-selects publishes
// optionValueSelectionChange, product-info.js disables the Add-to-cart button,
// re-fetches the whole section with ?variant=...&section_id=... and swaps
// fragments out of the response. On a good connection that is a 200-500ms
// window in which the price still shows the old size and the button cannot be
// pressed. It is the pause you feel when tapping a size.
//
// Nothing in this template actually needs the server for that. The price, the
// weight line, the quantity rule, the featured image and the in-stock flag are
// all known at render time and ship with the page (new-design-variant-data
// .liquid). So the change is applied synchronously in the change handler and
// the request is never made.
//
// The listener runs in the CAPTURE phase on <variant-selects>. variant-selects
// binds its own handler on itself in the bubble phase, so capture on the same
// element runs first and stopImmediatePropagation() keeps Dawn's path from
// starting. The radio is already checked by then -- the browser does that
// before firing the change event -- so nothing about the selection depends on
// the event continuing.
//
// Fallback: if the section renders anything this file does not know how to
// update -- a SKU line, an inventory line, volume pricing -- the event is left
// alone and Dawn's fetch runs exactly as before. The instant update still
// happens first, so even then the page reacts immediately and the response
// merely confirms it.
(() => {
  'use strict';

  // Slots only the server can fill. Their presence means the fetch has to stay.
  const SERVER_ONLY =
    '[id^="Sku-"], [id^="Inventory-"], [id^="Volume-"], [id^="Price-Per-Item-"]';

  const parseData = (root, sectionId) => {
    const node = root.querySelector('#VariantData-' + CSS.escape(sectionId));
    if (!node) return null;

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (error) {
      console.warn('variant-swap: could not read variant data', error);
      return null;
    }
  };

  // The option values currently chosen, in fieldset order -- which is the
  // order variant.options comes in.
  const selectedOptions = (selects) =>
    Array.from(selects.querySelectorAll('.product-form__input')).map((group) => {
      const radio = group.querySelector('input[type="radio"]:checked');
      if (radio) return radio.value;

      const select = group.querySelector('select');
      return select ? select.value : null;
    });

  const matchVariant = (variants, values) =>
    variants.find(
      (variant) =>
        variant.options.length === values.length &&
        variant.options.every((option, index) => option === values[index])
    ) || null;

  /* ---- the pieces of the page that carry a variant ---------------------- */

  // Only the text is rewritten, never the structure: the price block's markup
  // differs between a plain price and a discounted one, and rebuilding it here
  // would mean reimplementing snippets/price.liquid in JS. When the sale state
  // has to change shape, that is handed back to the server (see needsServer).
  const priceIsOnSale = (container) =>
    !!container && !!container.querySelector('.price--on-sale');

  const swapPrice = (container, variant) => {
    if (variant.on_sale) {
      container.querySelectorAll('.price-item--sale').forEach((el) => {
        el.textContent = variant.price;
      });
      container.querySelectorAll('.price-item--regular').forEach((el) => {
        el.textContent = variant.compare_at_price;
      });
      return;
    }

    container.querySelectorAll('.price-item--regular').forEach((el) => {
      el.textContent = variant.price;
    });
  };

  const swapWeight = (root, sectionId, variant) => {
    const slot = root.querySelector('#Weight-' + CSS.escape(sectionId));
    if (!slot) return;

    const line = slot.querySelector('[data-selected-variant-weight]');

    if (variant.weight) {
      if (line) {
        line.textContent = variant.weight;
      } else {
        const created = document.createElement('p');
        created.className = 'new-design-product__weight';
        created.setAttribute('data-selected-variant-weight', '');
        created.textContent = variant.weight;
        slot.appendChild(created);
      }
      slot.classList.remove('hidden');
      return;
    }

    // Removed rather than emptied: an empty paragraph still takes up its line.
    if (line) line.remove();
    slot.classList.add('hidden');
  };

  const swapQuantityRules = (productInfo, variant) => {
    const input = productInfo.querySelector('.quantity__input');
    if (!input) return;

    input.setAttribute('data-cart-quantity', String(variant.cart_qty || 0));
    input.setAttribute('data-min', String(variant.qty_min));
    input.setAttribute('step', String(variant.qty_step));

    if (variant.qty_max === null || variant.qty_max === undefined) {
      input.removeAttribute('data-max');
    } else {
      input.setAttribute('data-max', String(variant.qty_max));
    }

    // Recomputes min/max/value from the attributes just written.
    if (typeof productInfo.setQuantityBoundaries === 'function') {
      productInfo.setQuantityBoundaries();
    }
  };

  const swapSubmitButton = (productInfo, variant) => {
    const form = productInfo.querySelector('product-form');
    const soldOut =
      (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';

    if (form && typeof form.toggleSubmitButton === 'function') {
      form.toggleSubmitButton(!variant.available, soldOut);
      return;
    }

    const button = productInfo.querySelector('[type="submit"]');
    if (button) button.toggleAttribute('disabled', !variant.available);
  };

  const swapVariantInputs = (productInfo, variant) => {
    productInfo.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = String(variant.id);
      input.removeAttribute('disabled');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const swapMedia = (productInfo, sectionId, variant) => {
    if (!variant.media_id) return;

    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const mediaId = sectionId + '-' + variant.media_id;
    const target = gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!target || target.classList.contains('is-active')) return;

    gallery.setActiveMedia(mediaId, true);
  };

  const swapUrl = (productInfo, variant) => {
    if (productInfo.dataset.updateUrl === 'false') return;

    const share = productInfo.querySelector('share-button');
    if (share && typeof share.updateUrl === 'function') {
      share.updateUrl(new URL(variant.url, window.location.origin).href);
    }

    window.history.replaceState({}, '', variant.url);
  };

  /* ---- wiring ------------------------------------------------------------ */

  const attach = (productInfo) => {
    if (productInfo.dataset.instantVariant) return;

    const selects = productInfo.querySelector('variant-selects');
    if (!selects) return;

    const sectionId =
      productInfo.dataset.originalSection || productInfo.dataset.section;
    const variants = parseData(productInfo, sectionId);
    if (!variants) return;

    productInfo.dataset.instantVariant = 'true';

    selects.addEventListener(
      'change',
      (event) => {
        const variant = matchVariant(variants, selectedOptions(selects));

        // A combination that does not exist (possible with two or more
        // options) is what Dawn's setUnavailable() handles -- leave it to that.
        if (!variant) return;

        const priceContainer = productInfo.querySelector(
          '#price-' + CSS.escape(sectionId)
        );
        const priceIsLocal =
          !!priceContainer && priceIsOnSale(priceContainer) === variant.on_sale;

        if (priceIsLocal) swapPrice(priceContainer, variant);
        swapWeight(productInfo, sectionId, variant);
        swapVariantInputs(productInfo, variant);
        swapQuantityRules(productInfo, variant);
        swapSubmitButton(productInfo, variant);
        swapMedia(productInfo, sectionId, variant);
        swapUrl(productInfo, variant);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: sectionId, variant: variant },
          });
        }

        // Hand back to Dawn only for what could not be resolved here: a price
        // block whose sale state has to change shape, or a section carrying
        // server-rendered slots this file does not touch.
        const needsServer =
          !priceIsLocal || !!productInfo.querySelector(SERVER_ONLY);
        if (!needsServer) event.stopImmediatePropagation();
      },
      true
    );
  };

  const init = () => document.querySelectorAll('product-info').forEach(attach);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('product-info').forEach((el) => {
      delete el.dataset.instantVariant;
    });
    init();
  });
})();
